import React from 'react';
import { DateTime } from 'luxon';
import { Device } from '../../lib/types';
import type { RescueDeviceSortColumn, SortDir } from '../../lib/types';
import { formatDaysAgo, formatLastTextReminder } from '../../lib/utils';
import Button from '../../components/Button/Button';
import ActionMenu, { type ActionMenuEntry } from '../../components/ActionMenu/ActionMenu';
import DeviceCell from './DeviceCell';
import { ACTION_COLUMN_WIDTH } from './columnWidths';
import { SkeletonRows } from '../../components/SkeletonRow/SkeletonRow';
import Pagination from '../../components/Pagination/Pagination';
import CollapsibleSection from './CollapsibleSection';
import { sendSmsEntry } from './sendSmsEntry';
import TableHead, { type TableColumn } from './TableHead';
import PetNameCell from './PetNameCell';
import styles from './Devices.module.scss';

/** Columns the stalled table can be sorted by (clicking their header). */
export type StalledSortKey = Extract<RescueDeviceSortColumn, 'adopter' | 'pet' | 'stalled' | 'lastTextReminder'>;

// Action was 40% of this table — four buttons wide — for a section whose job is to show
// which adoptions have gone quiet. Behind one menu trigger it takes the tab's shared width,
// so it lines up with Pending above and Completed below; the lead columns fill the rest.
export const STALLED_COLUMNS: TableColumn<StalledSortKey>[] = [
  { label: 'Adopter', width: '22%', sortKey: 'adopter' },
  { label: 'Pet', width: '17%', sortKey: 'pet' },
  { label: 'Device', width: '18%' },
  { label: 'Stalled', width: '13%', sortKey: 'stalled' },
  { label: 'Last Text Reminder', width: '17%', sortKey: 'lastTextReminder' },
  { label: 'Action', width: `${ACTION_COLUMN_WIDTH}%`, hideLabel: true },
];

function LastTextReminder({ device }: { device: Device }) {
  if (device.smsOptedOut) {
    return <span className={styles.lastTextOptedOut}>Opted out</span>;
  }
  const { lastTextReminderAt } = device;
  let className: string | undefined;
  if (!lastTextReminderAt) {
    className = styles.textMuted;
  } else if (DateTime.fromISO(lastTextReminderAt).hasSame(DateTime.now(), 'day')) {
    className = styles.lastTextToday;
  }
  return <span className={className}>{formatLastTextReminder(lastTextReminderAt)}</span>;
}

interface StalledTransfersTableProps {
  devices: Device[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  /** Age threshold (in days) that qualifies a pending transfer as stalled. */
  thresholdDays: number;
  onPageChange: (page: number) => void;
  skeletonCount: number;
  sortKey: StalledSortKey;
  sortDir: SortDir;
  onSort: (key: StalledSortKey) => void;
  onMarkRecovered: (device: Device) => void;
  onNotRecovered: (device: Device) => void;
  onSendSms: (device: Device) => void;
  onCancelTransfer: (device: Device) => void;
  /** Text every stalled adopter, not just this page. Omit to leave the header without its actions. */
  onTextAll?: () => void;
  onEmailAll?: () => void;
}

export default function StalledTransfersTable({
  devices,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  thresholdDays,
  onPageChange,
  skeletonCount,
  sortKey,
  sortDir,
  onSort,
  onMarkRecovered,
  onNotRecovered,
  onSendSms,
  onCancelTransfer,
  onTextAll,
  onEmailAll,
}: StalledTransfersTableProps) {
  // Hide the section entirely when there are no stalled transfers and we're not
  // showing first-load skeletons. Keying off `loading` here would render an
  // empty "Stalled Transfers (0)" for the whole multi-page pending drain (which
  // keeps `loading` true); `skeletonCount` already covers the initial load.
  if (totalCount === 0 && skeletonCount === 0) {
    return null;
  }

  const hasData = devices.length > 0;
  const showOverlay = skeletonCount > 0 && hasData;
  const showSkeleton = skeletonCount > 0 && !hasData;
  const wrapperClass = `${styles.tableWrapper}${showOverlay ? ` ${styles.tableTransitioning}` : ''}`;

  // Chasing the adopter comes first — it's the step that might still save the adoption. Then
  // the two ways it ends, kept adjacent so they read as one either/or.
  function stalledActions(d: Device): ActionMenuEntry[] {
    return [
      { ...sendSmsEntry(d, 'Adopter'), onSelect: () => onSendSms(d) },
      { label: 'Mark Recovered', onSelect: () => onMarkRecovered(d) },
      { label: 'Not Recovered', onSelect: () => onNotRecovered(d) },
      { label: 'Cancel Transfer', onSelect: () => onCancelTransfer(d), danger: true, dividerBefore: true },
    ];
  }

  const headerAction =
    onTextAll && onEmailAll ? (
      <div className={styles.sectionHeaderButtons}>
        <Button variant="green" size="sm" onClick={onTextAll}>
          <svg
            className={styles.headerBtnIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Text all
        </Button>
        <Button variant="green" size="sm" onClick={onEmailAll}>
          <svg
            className={styles.headerBtnIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <polyline points="22 6 12 13 2 6" />
          </svg>
          Email all
        </Button>
      </div>
    ) : undefined;

  return (
    <CollapsibleSection
      title="Stalled"
      count={totalCount}
      variant="warning"
      description={`Adoption transfers still awaiting activation after ${thresholdDays} days. Reach out to the adopter, or recover the device if the adoption fell through.`}
      headerAction={headerAction}
    >
      <div className={wrapperClass}>
        <table className={styles.table}>
          <TableHead columns={STALLED_COLUMNS} sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
          <tbody>
            {showSkeleton ? (
              <SkeletonRows count={skeletonCount} columns={STALLED_COLUMNS.length} />
            ) : (
              devices.map((d) => (
                <tr key={d.id} className={styles.stalledRow}>
                  <td>
                    {d.adopterName}
                    <br />
                    <span className={styles.adopterEmail}>{d.adopterEmail}</span>
                  </td>
                  <td>{d.petName ? <PetNameCell name={d.petName} photoThumbUrl={d.petPhotoThumbUrl} /> : '—'}</td>
                  <td>
                    <DeviceCell moduleId={d.id} type={d.type} />
                  </td>
                  <td>
                    <span className={styles.stalledAge}>{formatDaysAgo(d.transferDate)}</span>
                  </td>
                  <td>
                    <LastTextReminder device={d} />
                  </td>
                  <td>
                    <ActionMenu items={stalledActions(d)} ariaLabel={`Actions for ${d.petName ?? d.id}`} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
